import { useMemo } from "react";
import { Deployment } from "../types/Deployment";
import { Device } from "../types/Device";
import { PipelineMetricMap } from "../types/Pipeline";
import { DeviceCounts } from "../pages/ReportLayout/Components/Report";
import { alignCounts, cumulativeSum, isCIEligible, scalingBiasCorrection } from "../types/Count";
import { toTemporalZonedDateTime, startOf, toDate } from "../shared/lib/temporal";

// Minimum manually-counted fish (upstream + downstream) required for a single
// device before its 95% CI is shown. 30 is the standard sample-size floor for
// the normal-approximation CI used downstream.
export const MIN_MANUAL_COUNT_FOR_CI = 30;

export const useProcessedCounts = (
    devices: Device[] | null,
    rawCountArray: any[] | null,
    pipelineMetricMap: PipelineMetricMap | null,
    deployment: Deployment | null,
    timezone: string,
    manualCountByDeviceId: Map<string, number> | undefined,
) => {
    const reuslts = useMemo(() => {
        if (
            !rawCountArray ||
            !devices ||
            !pipelineMetricMap ||
            !deployment ||
            !timezone ||
            manualCountByDeviceId === undefined
        ) {
            return { deviceCounts: undefined, ciEnabled: undefined };
        }

        let deviceCounts: DeviceCounts = {};

        // Deployment-level gate: as long as ANY device has enough manual
        // counts to anchor the bias correction, we treat CI as available for
        // the whole deployment. Per-device rendering still has to clear the
        // pipeline-metric eligibility check below — but we no longer require
        // every device to individually meet the manual-count threshold.
        const shoulEnableCIs = devices.some((d) => (manualCountByDeviceId.get(d.id) ?? 0) >= MIN_MANUAL_COUNT_FOR_CI);

        devices.forEach((device, index) => {
            const rawCounts = rawCountArray[index];
            if (rawCounts) {
                // Per-device CI rendering: pipeline-metric coverage for THIS
                // device, gated by the deployment-level manual-count check.
                // Once the deployment-level check passes, every device with
                // pipeline-metric coverage renders its CI bands — even ones
                // that don't have manual counts of their own.
                const ciEligible = shoulEnableCIs && isCIEligible(rawCounts, pipelineMetricMap);
                deviceCounts[device.name] = {
                    device,
                    counts: scalingBiasCorrection(rawCounts, pipelineMetricMap),
                    ciEligible,
                };
            }
        });

        const aligned = alignCounts(
            devices
                .map((device) => {
                    return deviceCounts[device.name]?.counts;
                })
                .filter((item) => item !== undefined),

            toDate(startOf(toTemporalZonedDateTime(deployment.startTime, timezone), "hour")),
            toDate(startOf(toTemporalZonedDateTime(deployment.endTime, timezone), "hour")),
        );

        devices.forEach((device, i) => {
            if (deviceCounts[device.name]) {
                deviceCounts[device.name].counts = aligned[i];
                deviceCounts[device.name].counts = cumulativeSum(deviceCounts[device.name].counts);
            }
        });

        // pre-sort devices by name.
        deviceCounts = Object.keys(deviceCounts)
            .sort()
            .reduce((obj: DeviceCounts, key) => {
                obj[key] = deviceCounts[key];
                return obj;
            }, {});

        return { counts: deviceCounts, ciEnabled: shoulEnableCIs };
    }, [rawCountArray, devices, pipelineMetricMap, deployment, timezone, manualCountByDeviceId]);

    return reuslts;
};
